<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { contactsApi } from '@/features/Contact/api/contactsApi';
import {
  isSgForeignAddressType,
  isSgLocalAddressType,
  SG_ADDR_FOREIGN,
  SG_ADDR_LOCAL,
} from './composables/shareholderAddressFields';

export type ShareholderAddressBlockVariant = 'residential' | 'contact';

const props = withDefaults(
  defineProps<{
    row: Record<string, unknown>;
    variant: ShareholderAddressBlockVariant;
    readOnly?: boolean;
    compact?: boolean;
    addressTypeItems?: Array<{ title: string; value: string }>;
  }>(),
  {
    readOnly: false,
    compact: false,
    addressTypeItems: () => [
      { title: 'Local Address', value: SG_ADDR_LOCAL },
      { title: 'Foreign Address', value: SG_ADDR_FOREIGN },
    ],
  },
);

const isResidential = computed(() => props.variant === 'residential');
const sectionTitle = computed(() =>
  isResidential.value ? 'Residential address' : 'Contact address',
);

const addressTypeCd = computed({
  get() {
    const r = props.row || {};
    const raw = isResidential.value
      ? trim(r.addressType)
      : trim(r.alternateAddressCd) || trim(r.addressType);
    return raw || SG_ADDR_LOCAL;
  },
  set(v: string) {
    const r = props.row;
    if (!r) return;
    if (isResidential.value) {
      r.addressType = v || SG_ADDR_LOCAL;
    } else {
      r.alternateAddressCd = v || SG_ADDR_LOCAL;
    }
  },
});

const isLocal = computed(() => isSgLocalAddressType(addressTypeCd.value));
const isForeign = computed(() => isSgForeignAddressType(addressTypeCd.value));

const postalCode = computed({
  get: () => trim(isResidential.value ? props.row?.postalCode : props.row?.altPostalCode),
  set: (v: string) => {
    if (!props.row) return;
    if (isResidential.value) props.row.postalCode = v;
    else props.row.altPostalCode = v;
  },
});
const blockNo = computed({
  get: () => trim(isResidential.value ? props.row?.blockNo : props.row?.altBlockNo),
  set: (v: string) => {
    if (!props.row) return;
    if (isResidential.value) props.row.blockNo = v;
    else props.row.altBlockNo = v;
  },
});
const streetName = computed({
  get: () => trim(isResidential.value ? props.row?.streetName : props.row?.altStreetName),
  set: (v: string) => {
    if (!props.row) return;
    if (isResidential.value) props.row.streetName = v;
    else props.row.altStreetName = v;
  },
});
const buildingName = computed({
  get: () => trim(isResidential.value ? props.row?.buildingName : props.row?.altBuildingName),
  set: (v: string) => {
    if (!props.row) return;
    if (isResidential.value) props.row.buildingName = v;
    else props.row.altBuildingName = v;
  },
});
const levelNo = computed({
  get: () => trim(isResidential.value ? props.row?.levelNo : props.row?.altAddrLevel),
  set: (v: string) => {
    if (!props.row) return;
    if (isResidential.value) props.row.levelNo = v;
    else props.row.altAddrLevel = v;
  },
});
const unitNo = computed({
  get: () => trim(isResidential.value ? props.row?.unitNo : props.row?.altAddrUnit),
  set: (v: string) => {
    if (!props.row) return;
    if (isResidential.value) props.row.unitNo = v;
    else props.row.altAddrUnit = v;
  },
});
const foreignLine1 = computed({
  get: () => trim(isResidential.value ? props.row?.foreignAddr1 : props.row?.altForeignAddr1),
  set: (v: string) => {
    if (!props.row) return;
    if (isResidential.value) props.row.foreignAddr1 = v;
    else props.row.altForeignAddr1 = v;
  },
});
const foreignLine2 = computed({
  get: () => trim(isResidential.value ? props.row?.foreignAddr2 : props.row?.altForeignAddr2),
  set: (v: string) => {
    if (!props.row) return;
    if (isResidential.value) props.row.foreignAddr2 = v;
    else props.row.altForeignAddr2 = v;
  },
});

const isSearchingPostal = ref(false);
const postalSearched = ref(false);
const postalError = ref<string | null>(null);

const retrievedLine = computed(() => {
  if (!postalSearched.value || !isLocal.value) return '';
  return [blockNo.value, streetName.value, buildingName.value].filter(Boolean).join(', ');
});

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

async function searchPostalCode() {
  const code = postalCode.value;
  if (!code) return;
  isSearchingPostal.value = true;
  postalError.value = null;
  try {
    const response = await contactsApi.searchPostalCode(code);
    const result = (response as { data?: { data?: Record<string, string> } })?.data?.data
      || (response as { data?: Record<string, string> })?.data;
    if (result) {
      streetName.value = trim(result.streetName);
      buildingName.value = trim(result.buildingEstateName || result.buildingName);
      blockNo.value = trim(result.blockHouseNo || result.blockNo);
      postalSearched.value = true;
    }
  } catch (error: unknown) {
    const err = error as { response?: { data?: { error?: { message?: string } | string; message?: string } }; message?: string };
    const responseData = err?.response?.data;
    let errorMessage = '';
    if (responseData?.error) {
      errorMessage =
        typeof responseData.error === 'object'
          ? responseData.error.message || ''
          : String(responseData.error);
    }
    postalError.value =
      errorMessage || responseData?.message || err?.message || 'Postal code search failed';
  } finally {
    isSearchingPostal.value = false;
  }
}

watch(postalCode, () => {
  postalSearched.value = false;
  postalError.value = null;
});
</script>

<template>
  <div class="sg-addr-block" :class="{ 'sg-addr-block--compact': compact }">
    <div v-if="!compact" class="sg-addr-block-title">{{ sectionTitle }}</div>
    <v-row dense>
      <v-col cols="12" md="6">
        <v-select
          v-model="addressTypeCd"
          :items="addressTypeItems"
          item-title="title"
          item-value="value"
          label="Address type"
          density="compact"
          variant="outlined"
          hide-details="auto"
          :readonly="readOnly"
        />
      </v-col>
    </v-row>

    <template v-if="isLocal">
      <v-alert
        v-if="postalError"
        type="error"
        density="compact"
        class="mb-2"
        closable
        @click:close="postalError = null"
      >
        {{ postalError }}
      </v-alert>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="postalCode"
            label="Postal code"
            density="compact"
            variant="outlined"
            hide-details="auto"
            :readonly="readOnly"
            :loading="isSearchingPostal"
          >
            <template v-if="!readOnly" #append-inner>
              <v-btn
                variant="tonal"
                color="primary"
                size="small"
                density="compact"
                :loading="isSearchingPostal"
                :disabled="!postalCode?.trim()"
                @click="searchPostalCode"
              >
                Retrieve
              </v-btn>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
      <div v-if="retrievedLine" class="sg-addr-retrieved text-caption text-medium-emphasis mb-2">
        {{ retrievedLine }}
      </div>
      <v-row dense>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="blockNo"
            label="Block / house no."
            density="compact"
            variant="outlined"
            hide-details="auto"
            :readonly="readOnly"
          />
        </v-col>
        <v-col cols="12" md="8">
          <v-text-field
            v-model="streetName"
            label="Street name"
            density="compact"
            variant="outlined"
            hide-details="auto"
            :readonly="readOnly"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="buildingName"
            label="Building name"
            density="compact"
            variant="outlined"
            hide-details="auto"
            :readonly="readOnly"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="levelNo"
            label="Level"
            density="compact"
            variant="outlined"
            hide-details="auto"
            :readonly="readOnly"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="unitNo"
            label="Unit"
            density="compact"
            variant="outlined"
            hide-details="auto"
            :readonly="readOnly"
          />
        </v-col>
      </v-row>
    </template>

    <template v-else-if="isForeign">
      <v-row dense>
        <v-col cols="12">
          <v-text-field
            v-model="foreignLine1"
            label="Foreign address line 1"
            density="compact"
            variant="outlined"
            hide-details="auto"
            :readonly="readOnly"
          />
        </v-col>
        <v-col cols="12">
          <v-text-field
            v-model="foreignLine2"
            label="Foreign address line 2"
            density="compact"
            variant="outlined"
            hide-details="auto"
            :readonly="readOnly"
          />
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<style scoped>
.sg-addr-block {
  margin-top: 10px;
  padding: 10px;
  border: 1px dashed #dce3ee;
  border-radius: 8px;
  background: #fafbfd;
}
.sg-addr-block--compact {
  margin-top: 0;
  padding: 0;
  border: none;
  background: transparent;
}
.sg-addr-block-title {
  font-size: 12px;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 8px;
}
.sg-addr-retrieved {
  padding-left: 4px;
}
</style>
